<script setup>
import { nextTick, onMounted, onUpdated, provide, watch } from 'vue';

const props = defineProps({ pageKey: { type: String, required: true }, visitKey: Number });
const emit = defineEmits(['ready']);
const presenters = new Set();
let revision = 0;

async function announce() {
  const current = ++revision;
  const pageKey = props.pageKey;
  const visitKey = props.visitKey;
  await nextTick();
  // Embedded legacy boundaries synchronously resolve their local loaders even
  // while an ancestor is hidden. Their matching skeleton must render first.
  // A failed compatibility adapter must not strand an otherwise mounted error
  // or content frame behind the navigation preview.
  const outcomes = await Promise.allSettled(
    [...presenters].map((prepare) => Promise.resolve().then(prepare)),
  );
  outcomes
    .filter((outcome) => outcome.status === 'rejected')
    .forEach((outcome) => {
      console.error('Could not prepare the embedded loading presentation.', outcome.reason);
    });
  await nextTick();
  if (current === revision && pageKey === props.pageKey && visitKey === props.visitKey) {
    emit('ready', { pageKey, visitKey });
  }
}

provide('bayhiveLoadingPresentation', {
  register(prepare) {
    presenters.add(prepare);
    void announce();
    return () => presenters.delete(prepare);
  },
});
watch(() => [props.pageKey, props.visitKey], announce, { flush: 'post' });
onMounted(announce);
onUpdated(announce);
</script>

<template>
  <div><slot /></div>
</template>
